import {useCallback, useContext, useMemo} from "react";
import {RegistrationsContext} from "../../../context/contextProvider";
import {CustomerTypeConfig, InsuranceTypeConfig, MarketSpecificFieldType} from "../../../types/onsiteConfig";
import {useTranslation} from "react-i18next";
import {useCurrentPartner} from "./useCurrentPartner";

export type CustomerTypeConfigWithDisplayDescription = InsuranceTypeConfig & {
  displayDescription: string;
};

export function useRegistrationConfigHook() {
  const {t} = useTranslation('registrations');
  const installationConfig = useContext(RegistrationsContext).config.installationConfig;
  const partnerSalesMarket = useCurrentPartner().market ?? '';
  
  const registrationDefaultValuesByCountry = useCallback((country: string) =>
    (installationConfig?.registrationDefaultValues ?? []).find((c) => c.salesMarket === country),
    [installationConfig]
  );

  const registrationDefaultValues = useMemo(() =>
    registrationDefaultValuesByCountry(partnerSalesMarket),
    [partnerSalesMarket, registrationDefaultValuesByCountry]
  );

  const configuredCustomerTypes = useMemo(() => 
    installationConfig?.customerTypes ?? [],
    [installationConfig]
  );

  const configuredInsurance = useMemo(() => 
    installationConfig?.insuranceTypes ?? [],
    [installationConfig]
  );
  
  const validCustomerTypes = useMemo(() =>
    configuredCustomerTypes.filter((customerType: CustomerTypeConfig) =>
      customerType.validForSalesMarkets.includes(partnerSalesMarket)
    ),
    [configuredCustomerTypes, partnerSalesMarket]
  );

  const requirePropertyType = useMemo(() =>
    !!(installationConfig?.marketSpecificFields.find(x => x.field === MarketSpecificFieldType.PropertyType)?.validForSalesMarkets.includes(partnerSalesMarket)),
    [configuredCustomerTypes, partnerSalesMarket]
  );

  const getAvailableInsurancesWithDescription = useCallback(
  (customerTypeCode: string): CustomerTypeConfigWithDisplayDescription[] => {
    customerTypeCode = customerTypeCode?.toLowerCase() ?? '';
    return configuredInsurance
      .filter(
        (insurance) =>
          (
            insurance.validCustomerCodes.some((validCustomerCode) => validCustomerCode.toLowerCase() === customerTypeCode)
            ||
            insurance.validCustomerTypes.some((validCustomerType) => validCustomerType.toLowerCase() === customerTypeCode)
          ) &&
          insurance.validForSalesMarkets.includes(partnerSalesMarket)
        )
      .map((insurance) => ({
        ...insurance,
        displayDescription: customerTypeCode === 'housingcooperative'
        ? `${t(`registrations.insuranceDescriptions.${insurance.displayName}`)} ${t(`registrations.insuranceDescriptions.insuranceNote`)}`
        : t(`registrations.insuranceDescriptions.${insurance.displayName}`)
      }));
    },
    [configuredInsurance, partnerSalesMarket, t]
  );

  const getCustomerTypeConfigurationByCode = useCallback((customerTypeCode: string) =>
    configuredCustomerTypes.find(c => c.code === customerTypeCode),
    [configuredCustomerTypes]
  );
  
  const isPrivateCustomerType = useCallback((customerTypeCode: string) =>
    // notInUse = Norway
    customerTypeCode.toLowerCase() === 'privateindividual' || customerTypeCode === 'private' || customerTypeCode.toLowerCase() === 'notinuse',
    []
  );
  
  return {
    registrationDefaultValues,
    configuredCustomerTypes,
    validCustomerTypes,
    registrationDefaultValuesByCountry,
    isPrivateCustomerType,
    getCustomerTypeConfigurationByCode,
    getAvailableInsurancesWithDescription,
    requirePropertyType
  }
}